/**
 * Meibi Quiz — recommendation engine
 *
 * ---- EDITABLE ZONE: quiz questions & tag matching rules ----
 * Add/edit/remove questions and answers here. Each answer's `tags` array
 * lists the exact Shopify product tags that answer should boost in the
 * results. An answer can carry an empty `tags` array when there is no
 * corresponding product tag in the catalog yet — it simply won't affect
 * scoring. Reorder questions by reordering this array; the quiz UI
 * follows this order automatically.
 */
const QUIZ_QUESTIONS = [
    {
        id: 'q_anatomy',
        text: 'First things first... what do you have?',
        answers: [
            { id: 'vulva', text: 'Vulva', tags: ['vaginal'] },
            { id: 'pene', text: 'Penis', tags: ['penis'] },
        ],
    },
    {
        id: 'q_for_whom',
        text: 'Who are you shopping for?',
        answers: [
            { id: 'solo', text: 'For myself', tags: [] },
            { id: 'shared', text: 'To share', tags: ['couples'] },
        ],
    },
    {
        id: 'q_experience',
        text: 'Would this be your first toy?',
        answers: [
            { id: 'first', text: "Yes, I've never had one", tags: ['beginner'] },
            { id: 'repeat', text: "No, I've had one before", tags: [] },
        ],
    },
];
/* ---- END EDITABLE ZONE ---- */

/**
 * Flattens the tags of every answer the shopper picked into one array.
 * @param {Array} questions - QUIZ_QUESTIONS
 * @param {Object} answersById - map of questionId -> answerId
 * @returns {Array<string>}
 */
function getSelectedTags(questions, answersById) {
    const tags = [];
    questions.forEach((question) => {
        const answerId = answersById[question.id];
        if (!answerId) return;
        const answer = question.answers.find((a) => a.id === answerId);
        if (answer) tags.push(...answer.tags);
    });
    return tags;
}

/**
 * Counts how many selected tags a product carries. One point per match;
 * a tag scores again for each answer that selected it.
 * @param {Object} product - {tags: string[], ...}
 * @param {Array<string>} selectedTags
 * @returns {number}
 */
function scoreProduct(product, selectedTags) {
    const productTags = product.tags || [];
    return selectedTags.reduce(
        (score, tag) => (productTags.indexOf(tag) !== -1 ? score + 1 : score),
        0
    );
}

/**
 * Ranks products by score, highest first. Ties keep their original
 * (collection) order because Array#sort is a stable sort in modern JS
 * engines — this is also what produces the "never empty" fallback: if
 * every product scores 0, the result is just the first `resultsCount`
 * products in collection order.
 * @param {Array<Object>} products
 * @param {Array<string>} selectedTags
 * @param {number} resultsCount
 * @returns {Array<Object>}
 */
function rankProducts(products, selectedTags, resultsCount) {
    return products
        .map((product) => ({ product, score: scoreProduct(product, selectedTags) }))
        .sort((a, b) => b.score - a.score)
        .slice(0, resultsCount)
        .map((entry) => entry.product);
}

/**
 * Wires up one `.Quiz` container: reads its embedded product JSON and
 * settings, then renders the first question. All state (current
 * question index, selected answers) lives in this closure only — no
 * storage, no globals, resets on every page load.
 * @param {HTMLElement} container
 */
function initQuiz(container) {
    const productsScript = container.querySelector('script[type="application/json"]');
    const products = productsScript ? JSON.parse(productsScript.textContent) : [];
    const resultsCount = parseInt(container.getAttribute('data-results-count'), 10) || 4;
    const resultsHeading =
        container.getAttribute('data-results-heading') || 'Your recommendations';
    const stage = container.querySelector('.Quiz__Stage');
    const progressContainer = container.querySelector('.Quiz__Progress');
    const progressBar = container.querySelector('.Quiz__ProgressBar');
    const progressStepEl = container.querySelector('.Quiz__ProgressStep');
    const progressPercentEl = container.querySelector('.Quiz__ProgressPercent');
    const startScreen = container.querySelector('[data-quiz-start]');
    const quizCard = container.querySelector('[data-quiz-card]');
    const startButton = container.querySelector('[data-quiz-start-button]');

    const state = { index: 0, answersById: {} };

    function renderQuestion() {
        const question = QUIZ_QUESTIONS[state.index];
        stage.innerHTML = '';

        const heading = document.createElement('h2');
        heading.className = 'Quiz__QuestionText';
        heading.textContent = question.text;
        stage.appendChild(heading);

        const answerList = document.createElement('div');
        answerList.className = 'Quiz__Answers';

        question.answers.forEach((answer) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'Quiz__AnswerButton';
            button.setAttribute(
                'aria-pressed',
                state.answersById[question.id] === answer.id ? 'true' : 'false'
            );

            const icon = document.createElement('span');
            icon.className = 'Quiz__AnswerIcon';
            icon.setAttribute('aria-hidden', 'true');
            icon.textContent = answer.text.charAt(0).toUpperCase();
            button.appendChild(icon);

            const label = document.createElement('span');
            label.className = 'Quiz__AnswerLabel';
            label.textContent = answer.text;
            button.appendChild(label);

            button.addEventListener('click', () => {
                state.answersById[question.id] = answer.id;
                renderQuestion();
            });
            answerList.appendChild(button);
        });

        stage.appendChild(answerList);
        stage.appendChild(renderNav(question));
        updateProgress();
    }

    function renderNav(question) {
        const nav = document.createElement('div');
        nav.className = 'Quiz__Nav';

        const back = document.createElement('button');
        back.type = 'button';
        back.className = 'Quiz__NavButton Quiz__NavButton--back';
        if (state.index > 0) {
            back.textContent = '‹ Back';
            back.addEventListener('click', () => {
                state.index -= 1;
                renderQuestion();
            });
        } else {
            back.textContent = '‹ Start';
            back.addEventListener('click', () => {
                if (quizCard) quizCard.hidden = true;
                if (startScreen) startScreen.hidden = false;
            });
        }
        nav.appendChild(back);

        const isLast = state.index === QUIZ_QUESTIONS.length - 1;
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'Quiz__NavButton Quiz__NavButton--next';
        next.textContent = isLast ? 'See Results ›' : 'Next Question ›';
        next.disabled = !state.answersById[question.id];
        next.addEventListener('click', () => {
            if (isLast) {
                renderResults();
            } else {
                state.index += 1;
                renderQuestion();
            }
        });
        nav.appendChild(next);

        return nav;
    }

    function updateProgress() {
        if (!progressBar) return;
        const total = QUIZ_QUESTIONS.length;
        const current = state.index + 1;
        const percent = Math.round((current / total) * 100);
        progressBar.style.width = percent + '%';
        if (progressStepEl) progressStepEl.textContent = 'Step ' + current + ' of ' + total;
        if (progressPercentEl) progressPercentEl.textContent = percent + '% complete';
        if (progressContainer) progressContainer.setAttribute('aria-valuenow', String(percent));
    }

    function renderResults() {
        const selectedTags = getSelectedTags(QUIZ_QUESTIONS, state.answersById);
        const ranked = rankProducts(products, selectedTags, resultsCount);

        stage.innerHTML = '';

        const heading = document.createElement('h2');
        heading.className = 'Quiz__ResultsHeading';
        heading.textContent = resultsHeading;
        stage.appendChild(heading);

        if (ranked.length === 0) {
            const empty = document.createElement('p');
            empty.className = 'Quiz__ResultsEmpty';
            empty.textContent = 'No products available right now.';
            stage.appendChild(empty);
        } else {
            const grid = document.createElement('div');
            grid.className = 'Quiz__ResultsGrid';
            ranked.forEach((product) => grid.appendChild(renderProductCard(product)));
            stage.appendChild(grid);
        }

        const retake = document.createElement('button');
        retake.type = 'button';
        retake.className = 'Quiz__RetakeButton';
        retake.textContent = 'Retake Quiz';
        retake.addEventListener('click', () => {
            state.index = 0;
            state.answersById = {};
            renderQuestion();
        });
        stage.appendChild(retake);

        if (progressBar) progressBar.style.width = '100%';
    }

    function renderProductCard(product) {
        const card = document.createElement('a');
        card.className = 'Quiz__ProductCard';
        card.href = product.url;

        if (product.image) {
            const image = document.createElement('img');
            image.className = 'Quiz__ProductImage';
            image.src = product.image;
            image.alt = product.title;
            image.loading = 'lazy';
            card.appendChild(image);
        }

        const title = document.createElement('p');
        title.className = 'Quiz__ProductTitle';
        title.textContent = product.title;
        card.appendChild(title);

        const price = document.createElement('p');
        price.className = 'Quiz__ProductPrice';
        price.textContent = product.price;
        card.appendChild(price);

        return card;
    }

    function startQuiz() {
        if (startScreen) startScreen.hidden = true;
        if (quizCard) quizCard.hidden = false;
        renderQuestion();
    }

    if (startButton) {
        startButton.addEventListener('click', startQuiz);
    } else {
        // No start screen in the markup (unexpected) — fall back to the old behavior.
        renderQuestion();
    }
}

if (typeof document !== 'undefined') {
    const bootQuizzes = (root) => {
        root.querySelectorAll('.Quiz').forEach((container) => {
            if (container.dataset.quizReady) return;
            container.dataset.quizReady = 'true';
            initQuiz(container);
        });
    };

    document.addEventListener('DOMContentLoaded', () => bootQuizzes(document));
    document.addEventListener('shopify:section:load', (event) => bootQuizzes(event.target));
}

const MeibiQuizEngine = {
    QUIZ_QUESTIONS,
    getSelectedTags,
    scoreProduct,
    rankProducts,
};

if (typeof module !== 'undefined' && module.exports) {
    module.exports = MeibiQuizEngine;
} else {
    window.MeibiQuizEngine = MeibiQuizEngine;
}
